/**
 * T-70: long comments get a short AI version (operation comment_short), shown in the task's activity
 * with More to read the whole comment. A comment counts as long by its prose: mention markup reads as
 * the name, and the factory's fenced blocks (questions, suggestions, steering options…) don't count,
 * as the activity draws them as one line or a card of their own, kept under the short version.
 */

/** Over this many characters of prose, a comment gets a short version. */
export const COMMENT_SHORT_MIN_CHARS = 600;

/** The longest short version kept; the prompt asks for well under it. */
export const COMMENT_SHORT_MAX_CHARS = 300;

const FENCE_OPEN_RE = /^ {0,3}(`{3,}|~{3,})[ \t]*([\w-]*)/;

export interface CommentParts {
  /** The comment without its factory blocks, trimmed. */
  prose: string;
  /** The factory blocks (```factory-…```), fences included, joined by blank lines; '' when none. */
  factoryBlocks: string;
}

/** Splits a comment into its prose and its factory blocks. Other fenced code stays in the prose. */
export function splitCommentParts(body: string): CommentParts {
  const lines = body.replace(/\r\n?/g, '\n').split('\n');
  const prose: string[] = [];
  const blocks: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const open = FENCE_OPEN_RE.exec(lines[i]);
    if (!open) {
      prose.push(lines[i]);
      i++;
      continue;
    }
    const [, marks, lang] = open;
    const close = new RegExp(`^ {0,3}${marks[0] === '`' ? '`' : '~'}{${marks.length},}[ \\t]*$`);
    const start = i;
    i++;
    while (i < lines.length && !close.test(lines[i])) i++;
    i++; // the closing fence (or the end)
    const block = lines.slice(start, Math.min(i, lines.length));
    if (lang.startsWith('factory-')) blocks.push(block.join('\n'));
    else prose.push(...block);
  }
  return { prose: prose.join('\n').trim(), factoryBlocks: blocks.join('\n\n') };
}

/** `@[Laura](user:…)` reads as `@Laura`. */
export function plainMentions(text: string): string {
  return text.replace(/@\[([^\]]+)\]\([a-z_]+:[^)]+\)/g, '@$1');
}

/** The comment's prose as it reads: no factory blocks, mentions as names. */
export function commentProse(body: string | null | undefined): string {
  return body ? plainMentions(splitCommentParts(body).prose) : '';
}

export function isLongComment(body: string | null | undefined): boolean {
  return commentProse(body).length > COMMENT_SHORT_MIN_CHARS;
}
